import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, PROPERTY_TYPES } from "@/lib/api";
import { useDemo } from "@/context/DemoContext";
import { useHousehold } from "@/context/HouseholdContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Buildings, ArrowRight, MapPin, WarningCircle } from "@phosphor-icons/react";
import { AddressFields, composeAddress, emptyAddress } from "@/components/AddressFields";
import { toast } from "sonner";

const emptyProp = { name: "", address_parts: { ...emptyAddress }, property_type: "single_family", current_value: "" };

export default function Properties() {
  const { fmtAsset } = useDemo();
  const { hid, isOperator, delinquentAssetIds, refreshAssets } = useHousehold();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyProp);
  const navigate = useNavigate();

  const load = useCallback(() => {
    if (!hid) return;
    api.get(`/assets?household_id=${hid}`)
      .then((r) => setItems(r.data.filter((a) => a.asset_type === "real_estate")))
      .catch(() => {});
  }, [hid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Property name is required"); return; }
    try {
      await api.post(`/assets?household_id=${hid}`, {
        name: form.name,
        asset_type: "real_estate",
        owner_party: "joint",
        address: composeAddress(form.address_parts),
        address_parts: form.address_parts,
        current_value: form.current_value ? Number(form.current_value) : 0,
        value_source: "manual",
        meta: { property_type: form.property_type },
      });
      toast.success("Property added");
      setOpen(false); setForm(emptyProp); load(); refreshAssets();
    } catch (e) { toast.error("Failed to add property"); }
  };

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Holdings · Real Estate</p>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Properties</h1>
        </div>
        {isOperator && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-property-button" className="rounded-sm font-semibold transition-transform hover:-translate-y-[1px]">
                <Plus size={16} weight="bold" className="mr-1" /> Add Property
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle className="font-display text-2xl font-black">New Property</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">Add a real-estate property to your household portfolio.</DialogDescription>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">Name</Label>
                  <Input data-testid="property-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. 123 Oak Street" className="rounded-sm mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">Type</Label>
                  <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                    <SelectTrigger data-testid="property-type-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PROPERTY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">Address</Label>
                  <div className="mt-1.5">
                    <AddressFields value={form.address_parts} idPrefix="property-address" onChange={(p) => setForm({ ...form, address_parts: p })} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">Current value</Label>
                  <Input data-testid="property-value-input" type="number" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} placeholder="0" className="rounded-sm mt-1.5 font-mono" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-property-button" onClick={submit} className="rounded-sm font-semibold w-full">Save Property</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <Buildings size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="font-display text-xl font-bold">No properties yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add your first property to start tracking documents, taxes, and performance.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((a) => {
            const delinquent = delinquentAssetIds?.has(a.id);
            return (
              <div
                key={a.id}
                data-testid={`property-card-${a.id}`}
                onClick={() => navigate(`/properties/${a.id}`)}
                className="bg-card border border-border rounded-sm p-5 cursor-pointer transition-all hover:shadow-md hover:-translate-y-[2px] group"
              >
                <div className="flex items-start justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-secondary px-2 py-1 rounded-sm">
                    {PROPERTY_TYPES[a.meta?.property_type] || "Real Estate"}
                  </span>
                  <ArrowRight size={18} className="text-muted-foreground group-hover:translate-x-1 transition-transform" />
                </div>
                <h3 className="font-display text-xl font-bold mt-4 tracking-tight">{a.name}</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                  <MapPin size={14} /> {a.address || "No location"}
                </p>
                {delinquent && (
                  <p data-testid={`property-tax-delinquent-${a.id}`} className="text-xs mt-2 inline-flex items-center gap-1.5 text-destructive font-semibold">
                    <WarningCircle size={13} weight="fill" /> Property tax delinquent
                  </p>
                )}
                <div className="mt-4 pt-4 border-t border-border flex justify-between items-end text-sm">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Value</p>
                  <p className="font-mono font-bold tabular-nums asset-value">{fmtAsset(a.current_value)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
